import {inject, Injectable} from './di';
import {AppInitializeService} from './app-initialize-service';
import {Logger} from './logger';
import {ConfigService} from './config/config-service';
import {StorageService} from './storage-service';
import {UrlsService} from './urls-service';
import type {Duration, Moment} from 'moment-timezone';
import moment from 'moment-timezone';

const TIME_OFFSET_LOCAL_STORAGE_KEY = 'time.offset';
const TIMEZONE_LOCAL_STORAGE_KEY = 'time.timezoneId';
const TIMEZONE_OFFSET_LOCAL_STORAGE_KEY = 'time.timezoneOffset';

/**
 * Service for working with time.
 * The current time and time zone of the device (TV), _server_ time (_true_ or _real_ time) and time zone from the
 * config may differ. To avoid these differences, you need to use this service. It allows you to get the real time,
 * time zone and manipulate the date and time taking these values into account.
 */
@Injectable()
export class TimeService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);
  private readonly storageService = inject(StorageService);
  private readonly urlsService = inject(UrlsService);

  /**
   * Time difference between current TV time and real time, in milliseconds.
   */
  private timeOffset: number = this.storageService.getNumber(TIME_OFFSET_LOCAL_STORAGE_KEY) || 0;

  /**
   * Application start time in local TV time.
   * Used for duration calculations.
   */
  private readonly startTime = new Date();

  /**
   * Real (server) Timezone ID.
   * This is guaranteed to be a valid timezone name for use with Moment.js.
   */
  private timezoneId: string =
    this.storageService.getString(TIMEZONE_LOCAL_STORAGE_KEY) || moment.tz.guess(true) || 'GMT';

  /**
   * A number representing the difference, in minutes, between the date as evaluated in the UTC time zone and as
   * evaluated in the real time zone.
   * For timezones GMT+N it will be negative, for GMT-N it will be positive.
   * @example `-480` // For 'Asia/Kuala_Lumpur' (GMT+8) timezone.
   * @example `0` // For GMT
   */
  private timezoneOffset: number =
    this.storageService.getNumber(TIMEZONE_OFFSET_LOCAL_STORAGE_KEY) || new Date().getTimezoneOffset();

  /**
   * Application uptime, in milliseconds.
   */
  public get uptime(): number {
    return Date.now() - this.startTime.getTime();
  }

  /**
   * Real (server) date/time, in moment.js format
   */
  public get now(): Moment {
    return moment(Date.now() + this.timeOffset).tz(this.timezoneId);
  }

  /**
   * Real (server) Timezone ID.
   * This is guaranteed to be a valid timezone name for use with Moment.js.
   */
  public get timezone(): string {
    return this.timezoneId;
  }

  constructor() {
    // TODO refactorLegacy values
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const timeService = this;
    window.timeService = this;
    window.time = {
      get date(): Date {
        return new Date(Date.now() + timeService.timeOffset - timeService.timezoneOffset * 60 * 1000);
      },

      get dof(): number {
        return timeService.timeOffset;
      },

      get tz(): number {
        return -timeService.timezoneOffset * 60 * 1000;
      },

      uptime: (human_readable?: boolean): number | string => {
        if (human_readable) {
          return timeService.formatDuration(moment.duration(timeService.uptime));
        }
        return timeService.uptime;
      },

      now: (utc?: boolean): number => {
        // NOTE: we can't change timezone of native Date objects, it is forced to be local device timezone. If server
        // timezone and local timezone are not the same, we need to handle it somehow. That's why this trick was made:
        // we just add timezone offset to the real current date value, so resulting date formatted for UTC will return
        // real server date.
        let out = Date.now() + timeService.timeOffset;
        if (!utc) {
          out -= timeService.timezoneOffset * 60 * 1000;
        }
        return out;
      },
    } as LegacyTimeObject;
  }

  public async init(): Promise<boolean> {
    this.logger.debug('[TimeService] Starting time synchronization with the server');

    await this.syncTime();

    this.runEventLoopThrottlingDetector();

    this.runUpdateUiLoop();

    return true;
  }

  /**
   * Real (server) date/time from Date object created locally via `new Date()` or `Date.now`.
   * @return moment.js date, localized and with timezone information.
   */
  public getRealDate(date: Date | number): Moment {
    if (typeof date !== 'number') {
      date = date.getTime();
    }
    return moment(date + this.timeOffset).tz(this.timezoneId);
  }

  /**
   * Synchronize server time and current TV local time.
   * This method avoids parallel calls.
   */
  private async syncTime() {
    try {
      let dateTimeUrl = this.urlsService.dateTimeUrl$.getValue();
      const timezone = this.configService.config?.timezone;

      if (!dateTimeUrl) {
        this.logger.error('[TimeService] Unable to sync time because dateTimeUrl is not defined in UrlsService');
        return;
      }

      // validate timezone from config, apply fallbacks
      if (timezone) {
        const zone = moment.tz.zone(timezone);
        if (zone) {
          this.timezoneId = zone.name;
        } else {
          this.timezoneId = moment.tz.guess() || 'GMT';
          this.logger.error(
            `[TimeService] App Config does contains invalid "timezone" setting: "${timezone}' is not a valid Timezone ID. Using ${this.timezoneId} timezone instead`,
          );
        }
      } else {
        this.timezoneId = moment.tz.guess() || 'GMT';
        this.logger.error(
          `[TimeService] App Config does not contain "timezone" setting! Using ${this.timezoneId} timezone instead`,
        );
      }

      if (dateTimeUrl.endsWith('/')) {
        dateTimeUrl = dateTimeUrl.slice(0, -1);
      }

      const url = `${dateTimeUrl}${this.timezoneId ? '?tz=' + encodeURIComponent(this.timezoneId) : ''}`;
      const requestStartTime = Date.now();
      let requestEndTime: number | undefined;

      // NOTE: for some reason, we got problems with fetch polyfill and this API call on Firefox 8. For some unknown
      //  reason it returns "[object ArrayBuffer]" instead of text. This is bug in polyfill compatibility with
      //  whatwg-fetch and core.js. So we use plain XHR here.

      const result = await new Promise<TimeSyncDto | undefined>((resolve) => {
        $.ajax({
          url: url,
          type: 'GET',
          dataType: 'json',
          success: (response: TimeSyncDto | undefined) => {
            if (response && response.time && response.offset != null && response.tz) {
              requestEndTime = Date.now();
              resolve(response);
            } else {
              this.logger.error(
                `[TimeService] Unable sync datetime with server: server response is not valid`,
                response,
              );
              resolve(undefined);
            }
          },
          error: (jqXHR, status, error) => {
            this.logger.error(
              `[TimeService] Unable sync datetime with server "${url}". Request error. ${jqXHR?.status} ${jqXHR?.statusText} ${status}`,
              error,
            );
            resolve(undefined);
          },
        });
      });

      if (result) {
        // NOTE: taking into account request duration. Kind of NTP protocol.
        const serverTime = parseInt(result.time, 10) * 1000;

        this.timeOffset = serverTime - Math.floor((requestEndTime! + requestStartTime) / 2);
        this.storageService.set(TIME_OFFSET_LOCAL_STORAGE_KEY, this.timeOffset);
        this.timezoneOffset = -Math.floor(result.offset / 60);

        // validate timezone from config, apply fallbacks
        const zone = moment.tz.zone(result.tz);
        if (zone) {
          this.timezoneId = zone.name;
        } else {
          this.logger.error(
            `[TimeService] The timezone received from the server "${result.tz}" is not valid, or information about it is not contained in the Moment Timezone. Using fallback: ${this.timezoneId}.`,
          );
          this.timezoneOffset = -this.now.utcOffset();
        }

        this.logger.debug(`[TimeService] Local TV time was synchronized with server`);
      } else {
        // Unable to sync datetime with server

        if (Date.now() < 1704067200000) {
          // Taking internal device time only if current js time is less than Jan 1, 2024.
          // This refers to bug when js date is Unix epoch start (in 1970s) for some reason.
          // In many cases internal device time is really not accurate for some reason, so we use it only to fix that bug.
          await this.useInternalDeviceDateTime();
        }

        this.timezoneOffset = -this.now.utcOffset();
      }

      this.storageService.set(TIMEZONE_LOCAL_STORAGE_KEY, this.timezoneId);
      this.storageService.set(TIMEZONE_OFFSET_LOCAL_STORAGE_KEY, this.timezoneOffset);
    } catch (err) {
      this.logger.error('[TimeService] Unable to get datetime from server. Unexpected error', err);
      this.timezoneOffset = -this.now.utcOffset();
    }

    this.logger.debug(`[TimeService] Time offset (real time minus local time): ${this.timeOffset.toString()} ms`);
    this.logger.debug(`[TimeService] Real time: ${this.now.toISOString()}`);
    this.logger.debug(`[TimeService] Local TV time: ${new Date().toISOString()}`);
    this.logger.debug(`[TimeService] Local TV timezone offset: ${new Date().getTimezoneOffset()} minutes`);
    this.logger.debug(`[TimeService] Real timezone offset: ${this.timezoneOffset} minutes`);
    this.logger.debug(`[TimeService] Real timezone ID: ${this.timezoneId}`);
  }

  /**
   * Try to use an internal device time if it is available.
   */
  private async useInternalDeviceDateTime() {
    try {
      this.logger.debug('[TimeService] Applying device time and timezone...');

      // get device time
      if (window._tv_get_time && typeof window._tv_get_time === 'function') {
        // _tv_get_time is a vendor module function
        const ms = await Promise.resolve(window._tv_get_time());

        // sometimes it also returns time near unix epoch time
        if (ms > 1000 * 60 * 60) {
          this.timeOffset = Date.now() - ms;
          this.storageService.set(TIME_OFFSET_LOCAL_STORAGE_KEY, this.timeOffset);

          this.logger.debug(`[TimeService] JS time was synchronized with device`);
        }
      }

      // get device timezone
      if (window._tv_get_timezone && typeof window._tv_get_timezone === 'function') {
        // _tv_get_timezone is a vendor module function
        const timezone = await Promise.resolve(window._tv_get_timezone());
        const zone = moment.tz.zone(timezone);
        if (zone) {
          if (this.timezoneId !== zone.name) {
            this.timezoneId = zone.name;
            this.logger.debug(`[TimeService] JS timezone was synchronized with device timezone`);
          }
        } else {
          this.logger.error(
            `[TimeService] The timezone received from the device "${timezone}" is not valid, or information about it is not contained in the Moment Timezone. Using fallback: ${this.timezoneId}.`,
          );
        }
      }
    } catch (err) {
      this.logger.error('[TimeService] Unable to apply device time', err);
    }
  }

  private runUpdateUiLoop() {
    setTimeout(() => {
      try {
        this.updateUi();
      } catch (err) {
        this.logger.error(`[TimeService] Unable to update time-related UI`, err);
      }

      this.runUpdateUiLoop();
    }, 1000);
  }

  private updateUi() {
    // TODO legacy, move to components!!!
    const config = this.configService.config;

    if (config.tv?.time_status_exist) {
      $('#tv_fullscreen_time').show();
    }

    const container = $('#tv_fullscreen_date');
    if (config.tv?.date_status_exist) {
      container.html(this.now.format('ll'));
      container.show();
    } else {
      container.hide();
    }

    const now = this.now;

    let hours: string;
    let minutesAndMeridiem: string;
    if (config.hour12 != null && typeof config.hour12 === 'boolean') {
      if (config.hour12) {
        hours = now.format('h');
        minutesAndMeridiem = now.format('mma');
      } else {
        hours = now.format('H');
        minutesAndMeridiem = now.format('mm');
      }
    } else {
      const localizedTimeParts = now.format('LT').split(':');
      hours = localizedTimeParts[0];
      minutesAndMeridiem = localizedTimeParts[1];
    }

    $('#tv_fullscreen_time_hours').html(hours);
    $('#tv_fullscreen_time_minutes').html(minutesAndMeridiem);

    if (config.tv_channellist_type === 'mosaic' && config.tv?.mosaic_clock) {
      $('#tv_fullscreen_mosaic_time').html(
        `<span class="ui-icon-clock"></span>${hours}<span class="blink">:</span>${minutesAndMeridiem}`,
      );
    }
  }

  /**
   * Sometimes the browser may start to throttle the tab (or webview) for various reasons unknown to us. In this case,
   * callbacks from the Event Loop queue may not be executed immediately, but no more often than once every N minutes.
   * This behavior can seriously break the application so we need to be able to track them.
   * This function tracks such situations.
   *
   * {@see https://developer.chrome.com/docs/devtools/settings/throttling?hl=en}
   */
  private runEventLoopThrottlingDetector() {
    const lastTimeMark = Date.now();
    const TIMEOUT = 60000;
    setTimeout(() => {
      const difference = Date.now() - lastTimeMark - TIMEOUT;
      if (Math.abs(difference) > 2000) {
        this.logger.warn(
          `[TimeService] Event loop throttling or system time change detected! Difference: ${difference} ms`,
        );
      }
      this.runEventLoopThrottlingDetector();
    }, TIMEOUT);
  }

  /**
   * Format time duration as human-readable string
   * @private
   */
  private formatDuration(duration: Duration) {
    const days = duration.days();
    const daysStr = days > 0 ? `${days} d` : '';
    return daysStr + moment.utc(duration.asMilliseconds()).format('HH[h] mm[m] ss[s]');
  }
}

interface TimeSyncDto {
  /**
   * Current timestamp, formatted as `ddd, DD MMM YY HH:mm:ss ZZ` (moment.js format notation)
   */
  datetime: string;

  /**
   * Timezone offset.
   * A number representing the difference, in seconds, between the date as evaluated in the real time zone and as
   * evaluated in the UTC time zone.
   * Note that this value has the opposite sign compared to `new Date().getTimezoneOffset()`.
   * For timezones GMT+N it will be positive, for GMT-N it will be negative.
   */
  offset: number;

  /**
   * Current time, integer number of seconds elapsed since the Unix epoch. The value is formatted as a string.
   */
  time: string;

  /**
   * Timezone ID.
   * @example "Asia/Kuala_Lumpur"
   */
  tz: string;
}

declare global {
  interface Window {
    /**
     * Legacy time utility service.
     * @deprecated Use TimeService instead.
     */
    time: LegacyTimeObject;

    /**
     * TimeService instance.
     */
    timeService: TimeService;
  }

  /**
   * Legacy time utility service.
   * @deprecated Use TimeService instead.
   */
  const time: LegacyTimeObject;

  /**
   * TimeService instance.
   */
  const timeService: TimeService;
}

/**
 * Legacy time utility service.
 */
interface LegacyTimeObject {
  /**
   * Legacy function to retrieve server date, but shifted by server timezone. So resulting date UTC values will be
   * the true time values.
   * To use it right you need to call UTC values like
   * @example window.time.date.getUTCHours() // to get current hour
   * @deprecated use `TimeService.now` instead, operating localized tz-aware Moment.js object.
   */
  get date(): Date;

  /**
   * Time difference between current TV time and real time, in milliseconds.
   * @deprecated Do not extract date logic from this service!
   */
  get dof(): number;

  /**
   * Timezone offset.
   * A number representing the difference, in seconds, between the date as evaluated in the real time zone and as
   * evaluated in the UTC time zone.
   * Note that this value has the opposite sign compared to `new Date().getTimezoneOffset()`.
   * For timezones GMT+N it will be positive, for GMT-N it will be negative.
   * @example `480` // For 'Asia/Kuala_Lumpur' (GMT+8) timezone.
   * @example `0` // For GMT
   * @deprecated Do not extract date logic from this service!
   */
  get tz(): number;

  /**
   * Get system uptime
   * @param human_readable get uptime as formatted human-readable string
   * @deprecated Use `TimeService.uptime` instead
   */
  uptime: (human_readable?: boolean) => number | string;

  /**
   * Legacy function to retrieve server date, but shifted by server timezone. So resulting date UTC values will be
   * the true time values.
   * To use it right you need to call UTC values like
   * @example new Date(window.time.now()).getUTCHours() // to get current hour
   *
   * @param utc force to use UTC timezone instead of server timezone
   * @deprecated Do not use this strange date representation! Use `TimeService.now` instead.
   */
  now: (utc?: boolean) => number;
}
